/**
 * The route table (`111-frontend-architecture.md` §7).
 *
 * React Router 8, declarative mode. No loaders, no actions — TanStack Query is the data
 * layer, and running two data layers side by side is how you get two caches that
 * disagree.
 *
 * Everything except /login sits inside <RequireAuth> → <AppShell>.
 *
 * ## What is lazy, and why (M7 — `120-non-functional.md` §2)
 *
 * The budget is **< 350 kB gzipped for the initial route**, and `120` names the remedy in the
 * same breath: *"Tiptap + ProseMirror is the bulk of it; the AI and Calendar routes are
 * lazy-loaded."* Until M7 nothing was, and the initial bundle was 481 kB gzipped — over budget
 * by 38%, with the calendar's three views, the AI workspace, `react-day-picker` and the
 * markdown renderer all shipped to a user who opened a page and never left it.
 *
 * So: **the editor is eager and everything else is not.** `/p/:pageId` is the main route, it is
 * where a boot lands (`RootRedirect`), and code-splitting the thing the user is already looking
 * at would only add a spinner to the one path that must feel instant. Every other route is a
 * `lazy()` — a navigation the user has explicitly asked for, which is exactly when a 20 ms chunk
 * fetch is invisible.
 *
 * `Suspense` sits INSIDE the shell, so the sidebar, the top bar and the sync indicator never
 * blink while a route chunk loads. `120` §1's rule that nothing is lost still holds: the outbox
 * and the drafts live above this boundary, in `OutboxBridge`.
 */
import { Suspense, lazy } from 'react';
import { Outlet, Route, Routes } from 'react-router';
import { AppShell } from '@/components/shell/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { SessionBridge } from '@/features/auth/SessionBridge';
import { NotFound } from '@/features/notebook/NotFound';
import { PageView } from '@/features/notebook/PageView';
import { RootRedirect } from '@/features/notebook/RootRedirect';
import { OutboxBridge } from '@/offline/OutboxBridge';

/* ── The lazy routes ────────────────────────────────────────────────────────── */

const Agenda = lazy(async () => ({ default: (await import('@/features/calendar/Agenda')).Agenda }));
const WeekView = lazy(async () => ({
  default: (await import('@/features/calendar/WeekView')).WeekView,
}));
const MonthView = lazy(async () => ({
  default: (await import('@/features/calendar/MonthView')).MonthView,
}));
const ChatPage = lazy(async () => ({ default: (await import('@/features/ai/ChatPage')).ChatPage }));
const TrashPage = lazy(async () => ({
  default: (await import('@/features/notebook/TrashPage')).TrashPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import('@/features/settings/SettingsPage')).SettingsPage,
}));
const PromptLibraryPage = lazy(async () => ({
  default: (await import('@/features/ai/PromptLibraryPage')).PromptLibraryPage,
}));
const TagView = lazy(async () => ({ default: (await import('@/features/tags/TagView')).TagView }));

/**
 * Deliberately not a spinner and not a skeleton (`110` §8: "a spinner tells you nothing").
 * A route chunk on a local network resolves in a handful of milliseconds; anything drawn here
 * would be a flash, which is worse than an empty pane for one frame.
 */
function RouteFallback() {
  return <div className="h-full" aria-busy="true" />;
}

export default function App() {
  return (
    <SessionBridge>
      <OutboxBridge>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<RootRedirect />} />

            {/* The editor. THE main route — and the one route that is NOT lazy. */}
            <Route path="/p/:pageId" element={<PageView />} />

            <Route
              element={
                <Suspense fallback={<RouteFallback />}>
                  <RouteOutlet />
                </Suspense>
              }
            >
              {/* The tag filter view (`070` §6). One tag at a time — multi-select is not v1. */}
              <Route path="/tags/:tagId" element={<TagView />} />

              {/* The calendar (`080`, `111` §7). The AGENDA is the default screen — the week and
                  month grids are the alternates, and their `?date=` lives in the URL so a view is
                  linkable and survives a reload. */}
              <Route path="/calendar" element={<Agenda />} />
              <Route path="/calendar/week" element={<WeekView />} />
              <Route path="/calendar/month" element={<MonthView />} />

              {/* The AI workspace (`090` §10, `111` §7). ONE component; the id selects the
                  thread. The slide-over (`Mod+E`) is the same component tree in the right pane. */}
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/:conversationId" element={<ChatPage />} />

              {/* M7. The trash as a ROOM, not the sidebar's peek at it (`060` §5): every archived
                  page with its children, restore, permanent delete, empty trash. */}
              <Route path="/trash" element={<TrashPage />} />

              {/* M7. Nine preferences, one screen (`100-settings.md`). */}
              <Route path="/settings" element={<SettingsPage />} />

              {/* The system-prompt library — the editor behind Settings → AI's "Manage the
                  prompt library" link (`090` §8, `100-settings.md` §5). */}
              <Route path="/settings/prompts" element={<PromptLibraryPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </OutboxBridge>
    </SessionBridge>
  );
}

/** A pathless layout route needs an outlet of its own to render its children into. */
function RouteOutlet() {
  return <Outlet />;
}
